import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '../styles/TenantRegistration.css';
import '../styles/nivaas-theme.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import nivaasLogo from '../assets/nivaas-logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://nivaas.app';

const TenantRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const planId = queryParams.get('plan');

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    admin_email: '',
    admin_password: '',
    admin_confirm_password: '',
    subscription_plan: '' // Will be set after fetching plans
  });

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [defaultsettings, setDefaultsettings] = useState([]);
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const settings_response = await axios.get('/api/saas/settings/default-settings');
        setDefaultsettings(settings_response.data);
      } catch (err) {
        console.log('error');
      }
    };

    fetchDefaultSettings();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const settings_response = await axios.get('/api/saas/settings/default-settings');
        console.log('settings_response', settings_response.data);

        const response = await axios.get(`${API_URL}/api/saas/subscriptions/plans`);
        const fetchedPlans = response.data;
        console.log('fetchedPlans', fetchedPlans);
        setPlans(fetchedPlans);

        let defaultPlan = null;

        // If planId is provided in URL, find the matching plan and set it
        if (planId) {
          defaultPlan = fetchedPlans.find(plan => plan.id.toString() === planId);
        }

        console.log(planId, 'planId');

        // If no planId or planId doesn't match, default to Free plan or first plan
        if (!planId) {
          const defaultPlanName = settings_response.data?.default_plan;
          defaultPlan = fetchedPlans.find(plan => plan.name === defaultPlanName) || fetchedPlans[0];
          console.log(defaultPlanName, 'defaultPlanName from settings');
        }

        if (defaultPlan) {
          setSelectedPlan(defaultPlan);
          setFormData(prevData => ({
            ...prevData,
            subscription_plan: defaultPlan.id.toString()
          }));
        } else {
          setSelectedPlan(defaultPlan);
          setFormData(prevData => ({
            ...prevData,
          }));
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to load subscription plans. Using default plans.');
        setLoading(false);
      }
    };

    fetchPlans();
  }, [planId]); // Add planId as dependency

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Update selected plan when subscription plan changes
    if (name === 'subscription_plan') {
      const plan = plans.find(p => p.id.toString() === value);
      setSelectedPlan(plan);
    }

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  const validateForm = () => {
    const errors = {};
    const trimmedName = formData.name.trim();
    const trimmedUsername = formData.username.trim();
    const trimmedEmail = formData.admin_email.trim();
    const trimmedPassword = formData.admin_password.trim();
    const trimmedConfirmPassword = formData.admin_confirm_password.trim();

    if (!trimmedName) {
      errors.name = 'Society name is required';
    }

    if (!trimmedUsername) {
      errors.username = 'Username is required';
    }

    if (!trimmedEmail) {
      errors.admin_email = 'Admin email is required';
    }
    else if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      errors.admin_email = 'Admin email must be valid';
    }

    if (!trimmedPassword) {
      errors.admin_password = 'Admin password is required';
    }
    else if (trimmedPassword.length < 8) {
      errors.admin_password = 'Password must be at least 8 characters';
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      errors.admin_confirm_password = 'Passwords do not match';
    }

    if (!formData.subscription_plan) {
      errors.subscription_plan = 'Please select a subscription plan';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      // Use the correct API endpoint with plan ID for backend compatibility
      const submitData = {
        ...formData,
        subscription_plan: formData.subscription_plan // Send the plan ID
      };

      const response = await axios.post(`${API_URL}/api/saas/tenants`, submitData);
      setSuccess(true);

      // Store subscription status and plan for the success message
      const isPaidPlan = selectedPlan && selectedPlan.name !== 'Free';
      const responseMessage = response.data.message;

      // Redirect to login after a short delay to show success UI
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: responseMessage || (isPaidPlan
              ? 'Your society has been registered successfully! An administrator will activate your account once payment is received.'
              : 'Your society has been registered successfully! You can now log in as an admin.')
          }
        });
      }, 3000);
    } catch (err) {

      if (err.response && err.response.data) {
        setError(err.response.data.message || 'Registration failed. Please try again.');

        // Handle specific errors
        if (err.response.status === 409) {
          if (err.response.data.message.includes('username')) {
            setFormErrors({
              ...formErrors,
              username: 'This username is already in use. Please choose another.'
            });
          } else if (err.response.data.message.includes('email')) {
            setFormErrors({
              ...formErrors,
              admin_email: 'This email is already in use. Please use a different email address.'
            });
          }
        }
      } else {
        setError('An error occurred during registration. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format price display
  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `₹${parseFloat(price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Format billing cycle display
  const formatBillingCycle = (cycle) => {
    return cycle === 'annual' ? 'annually' : 'monthly';
  };

  if (loading) {
    return (
      <div className="container">
        <div className="row justify-content-center mt-5">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-body p-5 text-center">
                <img
                  src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo}
                  alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                <div className="spinner-border mb-3" style={{ color: '#1F458E' }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Loading subscription plans...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-center mt-2">
        <div className="col-md-6">
          <div className="card shadow">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <img
                  src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo}
                  alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                <h2 className="h4 mb-2" style={{ color: '#1F458E', fontWeight: '600' }}>Create Your Society Portal</h2>
                <p className="text-muted">Join thousands of societies using Nivaas</p>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success text-center mb-3" role="alert">
                  <i className="fas fa-check-circle me-2"></i>
                  Registration successful! Redirecting to login...
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Society Name</label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your society's name"

                  />
                  {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                </div>

                <div className="mb-3">
                  <label htmlFor="username" className="form-label">Admin Username</label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.username ? 'is-invalid' : ''}`}
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Choose a username for admin login"

                  />
                  {formErrors.username && <div className="invalid-feedback">{formErrors.username}</div>}
                  <small className="text-muted">This username will be used for admin login</small>
                </div>

                <div className="mb-3">
                  <label htmlFor="admin_email" className="form-label">Admin Email</label>
                  <input
                    type="email"
                    className={`form-control ${formErrors.admin_email ? 'is-invalid' : ''}`}
                    id="admin_email"
                    name="admin_email"
                    value={formData.admin_email}
                    onChange={handleChange}
                    placeholder="admin@yoursociety.com"

                  />
                  {formErrors.admin_email && <div className="invalid-feedback">{formErrors.admin_email}</div>}
                </div>

                <div className="mb-3">
                  <label htmlFor="admin_password" className="form-label">Password</label>
                  <input
                    type="password"
                    className={`form-control ${formErrors.admin_password ? 'is-invalid' : ''}`}
                    id="admin_password"
                    name="admin_password"
                    value={formData.admin_password}
                    onChange={handleChange}
                    placeholder="••••••••"

                  />
                  {formErrors.admin_password && <div className="invalid-feedback">{formErrors.admin_password}</div>}
                  <small className="text-muted">Must be at least 8 characters</small>
                </div>

                <div className="mb-3">
                  <label htmlFor="admin_confirm_password" className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    className={`form-control ${formErrors.admin_confirm_password ? 'is-invalid' : ''}`}
                    id="admin_confirm_password"
                    name="admin_confirm_password"
                    value={formData.admin_confirm_password}
                    onChange={handleChange}
                    placeholder="••••••••"

                  />
                  {formErrors.admin_confirm_password && <div className="invalid-feedback">{formErrors.admin_confirm_password}</div>}
                </div>

                {/* Subscription Plan Section - Prominently displayed like in the screenshot */}
                <div className="mb-4">
                  <label className="form-label">Subscription Plan</label>
                  {selectedPlan && (
                    <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-medium text-dark">
                          {selectedPlan.name} {formatBillingCycle(selectedPlan.billing_cycle)}
                        </span>
                        <span className="fw-bold text-primary">
                          {formatPrice(selectedPlan.price)}{selectedPlan.price > 0 ? `/${formatBillingCycle(selectedPlan.billing_cycle)}` : ''}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Allow plan selection if needed */}
                  {plans.length > 1 && (
                    <div className="mt-2">
                      <select
                        className={`form-select ${formErrors.subscription_plan ? 'is-invalid' : ''}`}
                        id="subscription_plan"
                        name="subscription_plan"
                        value={formData.subscription_plan}
                        onChange={handleChange}

                      >
                        <option value="" disabled>Select a plan</option>
                        {plans.map(plan => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} - {formatPrice(plan.price)}{plan.price > 0 ? `/${formatBillingCycle(plan.billing_cycle)}` : ''}
                          </option>
                        ))}
                      </select>
                      {formErrors.subscription_plan && <div className="invalid-feedback">{formErrors.subscription_plan}</div>}
                    </div>
                  )}

                  {/* Payment info for paid plans */}
                  {selectedPlan && selectedPlan.price > 0 && (
                    <div className="alert alert-info mt-2">
                      <small>
                        <i className="fas fa-info-circle me-2"></i>
                        The <strong>{selectedPlan.name}</strong> plan requires payment of <strong>{formatPrice(selectedPlan.price)}/{formatBillingCycle(selectedPlan.billing_cycle)}</strong>.
                        Your account will be activated after payment is received and verified by our team.
                      </small>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn w-100 py-2"
                  style={{ backgroundColor: '#1F458E', color: 'white' }}
                  disabled={isSubmitting || success}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating Your Society...
                    </>
                  ) : success ? (
                    <>
                      <i className="fas fa-check me-2"></i>
                      Registration Successful
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus me-2"></i>
                      Create Society Portal
                    </>
                  )}
                </button>
                <button
                  type="submit"
                  className="btn w-100 py-2 mt-2"
                  style={{ backgroundColor: '#1F458E', color: 'white' }}
                  onClick={() => navigate('/')}
                >Back to Home
                </button>
              </form>
            </div>
          </div>

          {/* Login Link Footer */}
          <div className="text-center mt-3">
            <small className="text-muted">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-decoration-none"
                style={{ color: '#1F458E', fontWeight: '500' }}
              >
                Sign in here
              </Link>
            </small>
          </div>

          {/* Privacy Policy Footer */}
          <div className="text-center mt-2">
            <small className="text-muted">
              By creating an account, you agree to our{' '}
              <Link to="/privacy-policy" className="text-decoration-none" style={{ fontSize: '11px' }}>
                <i className="fas fa-shield-alt me-1"></i>
                Privacy Policy & Terms
              </Link>
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantRegistration; 